import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import AppUrl from '../../restApi/AppUrl';
import IRestClient from '../../restApi/IRestClient';
import ReactHtmlParser from 'react-html-parser';
import Wentwrong from '../Wentwrong/Wentwrong';
import { FaCalendarAlt, FaMapMarkerAlt, FaArrowRight, FaHeart, FaUsers, FaGlobe, FaStar, FaHandHoldingHeart } from 'react-icons/fa';
import { motion } from 'framer-motion';
import SectionHeader from '../SectionHeader/SectionHeader';

class Event extends Component {
  constructor() {
    super();
    this.state = {
      myData: [],
      error: false,
      loading: true
    };
  }

  componentDidMount() {
    IRestClient.GetRequest(AppUrl.Eventshome).then(result => {
      if (result == null) {
        this.setState({ error: true, loading: false });
      } else {
        this.setState({ myData: result, loading: false });
      }
    }).catch(() => {
      this.setState({ error: true, loading: false });
    });
  }

  getEventStyle = (eventTitle) => {
    if (!eventTitle || typeof eventTitle !== 'string') {
      return { icon: FaHandHoldingHeart, color: 'primary' };
    }
    const title = eventTitle.toLowerCase();
    if (title.includes('fundraiser') || title.includes('donation'))
      return { icon: FaHeart, color: 'secondary' };
    if (title.includes('volunteer') || title.includes('help'))
      return { icon: FaUsers, color: 'primary' };
    if (title.includes('awareness') || title.includes('education'))
      return { icon: FaGlobe, color: 'secondary' };
    if (title.includes('celebration') || title.includes('success'))
      return { icon: FaStar, color: 'tertiary' };
    return { icon: FaHandHoldingHeart, color: 'primary' };
  };

  getCountdownBadge = (dateStr) => {
    if (!dateStr) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const eventDate = new Date(dateStr);
    eventDate.setHours(0, 0, 0, 0);
    const diffDays = Math.round((eventDate - today) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return { label: 'Today!', classes: 'bg-green-500 text-white' };
    if (diffDays === 1) return { label: 'Tomorrow', classes: 'bg-blue-500 text-white' };
    if (diffDays <= 7) return { label: `In ${diffDays} days`, classes: 'bg-accent-600 text-white' };
    if (diffDays <= 30) return { label: `In ${diffDays} days`, classes: 'bg-primary-700 text-white' };
    return null;
  };

  render() {
    const { myData, loading, error } = this.state;

    if (error) return <Wentwrong />;

    if (loading) {
      return (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="animate-pulse bg-gray-200 rounded-full h-7 w-48 mx-auto mb-4" />
              <div className="animate-pulse bg-gray-200 rounded-xl h-12 w-72 mx-auto mb-4" />
              <div className="animate-pulse bg-gray-200 rounded h-4 w-96 mx-auto" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  <div className="animate-pulse bg-gray-200 h-52 w-full" />
                  <div className="p-6">
                    <div className="animate-pulse bg-gray-200 rounded h-6 w-3/4 mb-3" />
                    <div className="animate-pulse bg-gray-200 rounded h-4 w-full mb-2" />
                    <div className="animate-pulse bg-gray-200 rounded h-4 w-5/6 mb-4" />
                    <div className="animate-pulse bg-gray-200 rounded h-4 w-1/2 mb-5" />
                    <div className="animate-pulse bg-gray-200 rounded-lg h-10 w-32" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      );
    }

    // Hide section entirely when no upcoming events
    if (!myData || myData.length === 0) return null;

    return (
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Section Header */}
          <SectionHeader
            badge="Events & Activities"
            title="Upcoming Events"
            subtitle="Join us at our community events and help us make a meaningful difference together."
            variant="light"
            align="center"
            className="mb-12"
          />

          {/* Events Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {myData.map((event, index) => {
              if (!event) return null;
              const eventTitle = event.event_title || 'Untitled Event';
              const eventDescription = event.event_description || 'No description available';
              const eventImage = event.event_image || '/default-event-image.jpg';
              const eventDate = event.date;
              const eventLocation = event.event_location || 'Location TBD';
              const eventId = event.id || index;
              const countdown = this.getCountdownBadge(eventDate);

              return (
                <motion.div
                  key={eventId}
                  className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-neutral-100"
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -6 }}
                >
                  {/* Image */}
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={eventImage}
                      alt={eventTitle}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60" />

                    {/* Countdown badge — top-right */}
                    {countdown && (
                      <div className={`absolute top-3 right-3 text-xs font-bold px-3 py-1 rounded-full shadow-lg ${countdown.classes}`}>
                        {countdown.label}
                      </div>
                    )}

                    {/* Date badge — bottom-left */}
                    <div className="absolute bottom-3 left-3 bg-white bg-opacity-95 backdrop-blur-sm rounded-xl px-3 py-2 shadow-lg">
                      <div className="flex items-center gap-2">
                        <FaCalendarAlt className="text-secondary-600 text-sm" />
                        <span className="text-primary-800 text-xs font-semibold">
                          {eventDate
                            ? new Date(eventDate).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })
                            : 'Date TBD'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-primary-900 mb-2 line-clamp-2 group-hover:text-secondary-700 transition-colors duration-300">
                      {eventTitle}
                    </h3>

                    <p className="text-sm text-neutral-500 mb-4 leading-relaxed">
                      {ReactHtmlParser(eventDescription.substring(0, 100))}
                      {eventDescription.length > 100 && '...'}
                    </p>

                    {/* Location */}
                    <div className="flex items-center gap-2 text-sm text-neutral-500 mb-5">
                      <FaMapMarkerAlt className="text-secondary-500 flex-shrink-0" />
                      <span className="truncate">{eventLocation}</span>
                    </div>

                    {/* Button */}
                    <Link
                      to={`/eventdetails/${eventId}/${eventTitle}`}
                      className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2.5 px-5 rounded-xl transition-all duration-300 text-sm shadow-md hover:shadow-lg group"
                    >
                      <span>Learn More</span>
                      <FaArrowRight className="text-xs transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* View All Events CTA */}
          <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Link
              to="/allevents"
              className="inline-flex items-center gap-2 border-2 border-primary-600 text-primary-600 hover:bg-primary-600 hover:text-white font-semibold py-3 px-8 rounded-xl transition-all duration-300 text-sm"
            >
              <span>View All Events</span>
              <FaArrowRight className="text-xs" />
            </Link>
          </motion.div>

        </div>
      </section>
    );
  }
}

export default Event;
